import React, { useCallback } from 'react';
import {
  Box,
  Button,
  ButtonText,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { useMaybeRoomContext } from '@livekit/components-react';
import { useTranslation } from 'react-i18next';

import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { OralExamConductPhase } from '@/common/api/models/oralExamConductPhase';
import { OralExamPauseReason } from '@/common/api/models/oralExamPauseReason';
import { OralExamSupervisorStatus } from '@/common/api/models/oralExamSupervisorStatus';
import { OralExamMediaSource } from '@/common/api/models/oralExamMediaSource';
import { resolveFaultPresentation } from '@/common/oralExam/faultPresentation';
import { shouldShowLecturerExaminerUnavailable } from '@/common/hooks/oralExam/useExaminerAgentWatchdog';
import { useOralExamQuestionTimer } from '@/common/hooks/oralExam/useOralExamQuestionTimer';

import { voidReasonLine } from './voidReasonLine';

/** Aborted runs voided by connection loss or integrity timeout — rebook, not "ended early". */
export function isOralExamVoidRun(
  runState: { state?: string | null; endReason?: string | null },
): boolean {
  return runState.state === 'aborted'
    && (runState.endReason === 'agent_unavailable' || runState.endReason === 'session_void');
}

export type StudentStatusCardProps = {
  summary: OralExamBookingRunSummary;
  onNext?: (bookingId: string) => void;
};

type StudentStatusInfoProps = StudentStatusCardProps & {
  isObserving?: boolean;
  onObserveToggle?: (bookingId: string) => void;
};

function TileObserveButtonInner({
  bookingId,
  isObserving,
  onObserveToggle,
  observingLabel,
  observingVariant = 'outline',
  observingAction = 'secondary',
  isDisabled = false,
  observeError,
}: {
  bookingId: string;
  isObserving: boolean;
  onObserveToggle: (id: string) => void;
  observingLabel?: string;
  observingVariant?: 'solid' | 'outline';
  observingAction?: 'primary' | 'secondary';
  isDisabled?: boolean;
  observeError?: string | null;
}) {
  const { t } = useTranslation();
  const room = useMaybeRoomContext();

  const handlePress = useCallback(async () => {
    const willObserve = !isObserving;
    onObserveToggle(bookingId);
    if (willObserve && room) {
      await room.startAudio().catch(() => {});
    }
  }, [bookingId, isObserving, onObserveToggle, room]);

  return (
    <VStack space="xs" alignItems="flex-start">
      <Button
        size="sm"
        variant={isObserving ? observingVariant : 'outline'}
        action={isObserving ? observingAction : 'secondary'}
        onPress={handlePress}
        isDisabled={isDisabled}
        testID={`card-listen-${bookingId}`}
      >
        <ButtonText>
          {isObserving
            ? (observingLabel
              ?? t('oralExam.lecturer.cohort.backToOverview', { defaultValue: 'Return to overview' }))
            : t('oralExam.lecturer.cohort.observe', { defaultValue: 'Observe' })}
        </ButtonText>
      </Button>
      {observeError ? (
        <Text size="xs" color="$statusError" testID={`card-listen-error-${bookingId}`}>
          {observeError}
        </Text>
      ) : null}
    </VStack>
  );
}

/** Observe control; startAudio runs when LiveKitRoom is connected (may load after refresh). */
export function TileObserveButton({
  bookingId,
  isObserving,
  onObserveToggle,
  observingLabel,
  observingVariant,
  observingAction,
  isDisabled,
  observeError,
}: {
  bookingId: string;
  isObserving: boolean;
  onObserveToggle?: (id: string) => void;
  observingLabel?: string;
  observingVariant?: 'solid' | 'outline';
  observingAction?: 'primary' | 'secondary';
  isDisabled?: boolean;
  observeError?: string | null;
}) {
  if (!onObserveToggle) return null;
  return (
    <TileObserveButtonInner
      bookingId={bookingId}
      isObserving={isObserving}
      onObserveToggle={onObserveToggle}
      observingLabel={observingLabel}
      observingVariant={observingVariant}
      observingAction={observingAction}
      isDisabled={isDisabled}
      observeError={observeError}
    />
  );
}

/** @deprecated Use TileObserveButton — kept for incremental refactors. */
export const TileListenButton = TileObserveButton;

type StatusTone = 'agentFault' | 'help' | 'paused' | 'done' | 'integrity';

const SOURCE_LABEL: Record<OralExamMediaSource, { key: string; defaultValue: string }> = {
  camera: { key: 'oralExam.lecturer.cohort.integrity.camera', defaultValue: 'camera' },
  microphone: { key: 'oralExam.lecturer.cohort.integrity.microphone', defaultValue: 'microphone' },
  screen: { key: 'oralExam.lecturer.cohort.integrity.screen', defaultValue: 'screen sharing' },
  connection: { key: 'oralExam.lecturer.cohort.integrity.connection', defaultValue: 'connection' },
  recording: { key: 'oralExam.lecturer.cohort.integrity.recording', defaultValue: 'recording' },
};

/** Human-readable outage duration ("45s", "1m 05s"). */
export function formatOutageDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${(s % 60).toString().padStart(2, '0')}s`;
}

/**
 * Media-integrity breach for a student, or null when every required source is
 * intact. Derived from the server's `mediaIntegrity` summary (canonical): which
 * sources are currently down and the longest running outage in seconds.
 */
export function mediaIntegrityStatus(
  summary: OralExamBookingRunSummary,
): { sources: OralExamMediaSource[]; seconds: number } | null {
  const mi = summary.runState.mediaIntegrity;
  if (!mi || mi.intact !== false) return null;
  const current = mi.current ?? [];
  if (current.length === 0) return null;
  const seconds = Math.max(...current.map((c) => c.sinceSeconds ?? 0));
  return { sources: current.map((c) => c.source as OralExamMediaSource), seconds };
}

/**
 * The single most meaningful supervision state for a student, or null when the
 * student is simply on track. Replaces the old raw enum-chip dump so the wall
 * surfaces one actionable signal per student (2026-06-27 delta #4).
 *
 * Operational faults take priority over pacing states: if the AI examiner has
 * failed (or the run was flagged for failover), the supervisor must know first
 * so they can drop in or abort before the student is stranded (P8b).
 */
export function studentStatusLine(
  summary: OralExamBookingRunSummary,
  nowMs: number = Date.now(),
): { tone: StatusTone; key: string; defaultValue: string } | null {
  const { runState } = summary;
  const capacity = runState.capacity ?? 1;
  if (capacity > 1 && isOralExamVoidRun(runState)) {
    const line = voidReasonLine(runState);
    return {
      tone: 'agentFault',
      key: line.key,
      defaultValue: line.defaultValue,
    };
  }
  const fault = runState.operationalFault;
  if (fault) {
    const { lecturerLine } = resolveFaultPresentation(fault, { capacity });
    return {
      tone: lecturerLine.tone,
      key: lecturerLine.copyKey,
      defaultValue: lecturerLine.defaultValue,
    };
  }
  if (capacity <= 1 && shouldShowLecturerExaminerUnavailable(runState, nowMs)) {
    return {
      tone: 'agentFault',
      key: 'oralExam.lecturer.cohort.stateExaminerUnavailable',
      defaultValue: 'AI examiner unavailable — take over',
    };
  }
  const reason = (runState.pauseReason ?? '').toLowerCase();
  if (runState.isPaused && (reason.includes('help') || reason.includes('hand'))) {
    return { tone: 'help', key: 'oralExam.lecturer.cohort.stateRaisedHand', defaultValue: 'Raised hand — HELP' };
  }
  if (runState.studentDoneAt) {
    return { tone: 'done', key: 'oralExam.lecturer.cohort.stateDone', defaultValue: 'Done — awaiting next' };
  }
  if (runState.isPaused) {
    return { tone: 'paused', key: 'oralExam.lecturer.cohort.statePaused', defaultValue: 'Paused' };
  }
  return null;
}

const TONE_COLOR: Record<StatusTone, string> = {
  agentFault: '$statusError',
  help: '$statusWarning',
  paused: '$statusWarning',
  done: '$primary600',
  integrity: '$statusError',
};

export function studentMonogram(summary: OralExamBookingRunSummary): string {
  const name = summary.studentName?.trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  return summary.studentId.slice(0, 2).toUpperCase();
}

export function studentDisplayName(summary: OralExamBookingRunSummary): string {
  const name = summary.studentName?.trim();
  return name || summary.studentId;
}

/** First word only. "Mary Ann Smith" is "Mary". */
export function studentFirstName(summary: OralExamBookingRunSummary): string {
  const name = studentDisplayName(summary);
  const space = name.search(/\s/);
  return space === -1 ? name : name.slice(0, space);
}

/** Take over is not a product action. The lecturer never becomes the examiner. */
export function shouldOfferTakeOver(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  summary: OralExamBookingRunSummary,
): boolean {
  return false;
}

/**
 * When the fault's recovery is "leave" (e.g. session_expired), dropping into a
 * dead session is pointless — hide Drop in so the supervisor uses tile overflow Remove.
 */
export function shouldRemoveInsteadOfDropIn(summary: OralExamBookingRunSummary): boolean {
  const fault = summary.runState.operationalFault;
  if (!fault) return false;
  return resolveFaultPresentation(fault, { capacity: summary.runState.capacity }).recoveryCta === 'leave';
}

/**
 * Presentational status block for one student: name, progress, countdown,
 * status chips, and Next / take-over actions. The video tile and the
 * (legacy) monogram card both compose this so the status info stays consistent
 * (oral-exam-1n-lecturer-ux §3.4).
 */
export function StudentStatusInfo({
  summary,
  onNext,
  isObserving = false,
  onObserveToggle,
  detailsOnly = false,
}: StudentStatusInfoProps & { detailsOnly?: boolean }) {
  const { t } = useTranslation();
  const { bookingId, runState } = summary;
  const inExamConduct = runState.conductPhase === OralExamConductPhase.in_exam;
  const { formatted: timerLabel } = useOralExamQuestionTimer(
    runState.secondsPerQuestion,
    inExamConduct,
    runState.questionDeadline ?? null,
    runState.isPaused ?? false,
  );

  const integrity = mediaIntegrityStatus(summary);
  const helpRequested = runState.pauseReason === OralExamPauseReason.help_requested;
  // Integrity breach outranks the generic pause line (a media_disabled pause IS
  // the breach) and carries the actionable sources + duration.
  const statusLine = integrity ? null : studentStatusLine(summary);
  const hideObserveForRung = summary.supervisorStatus === OralExamSupervisorStatus.here_with_you
    || summary.supervisorStatus === OralExamSupervisorStatus.engaged;
  const sourcesLabel = integrity
    ? integrity.sources
      .map((s) => t(SOURCE_LABEL[s].key, { defaultValue: SOURCE_LABEL[s].defaultValue }))
      .join(', ')
    : '';

  return (
    <VStack flex={detailsOnly ? undefined : 1} space="xs">
      {detailsOnly ? null : (
        <Text size="sm" color="$textHeading" fontWeight="semibold">
          {studentDisplayName(summary)}
        </Text>
      )}
      {integrity ? (
        <VStack space="xs" testID={`student-integrity-${bookingId}`}>
          <Text size="xs" fontWeight="bold" color={TONE_COLOR.integrity}>
            {t('oralExam.lecturer.cohort.integrity.notIntact', {
              defaultValue: 'Integrity not intact — {{sources}} off for {{duration}}',
              sources: sourcesLabel,
              duration: formatOutageDuration(integrity.seconds),
            })}
          </Text>
          <Text size="2xs" color="$textSubtle">
            {t('oralExam.lecturer.cohort.integrity.guidance', {
              defaultValue: 'Drop in to assist, or remove from exam if it is not restored.',
            })}
          </Text>
        </VStack>
      ) : null}
      {statusLine ? (
        <Text size="xs" fontWeight="bold" color={TONE_COLOR[statusLine.tone]} testID={`student-state-${bookingId}`}>
          {t(statusLine.key, { defaultValue: statusLine.defaultValue })}
        </Text>
      ) : null}
      {inExamConduct && !detailsOnly ? (
        <HStack space="sm" flexWrap="wrap" alignItems="center" testID={`student-status-progress-${bookingId}`}>
          <Text size="xs" color="$textSubtle" testID={`student-status-timer-${bookingId}`}>
            {t('oralExam.lecturer.cohort.timeRemaining', {
              defaultValue: '{{time}} left',
              time: timerLabel,
            })}
          </Text>
        </HStack>
      ) : null}
      {detailsOnly ? null : (
        <HStack space="sm" flexWrap="wrap">
          {hideObserveForRung ? null : (
            <TileObserveButton
              bookingId={bookingId}
              isObserving={isObserving}
              onObserveToggle={onObserveToggle}
            />
          )}
          {onNext && !helpRequested ? (
            <Button
              size="sm"
              variant="outline"
              action="secondary"
              onPress={() => onNext(bookingId)}
              testID={`card-next-${bookingId}`}
            >
              <ButtonText>
                {t('oralExam.lecturer.cohort.next', { defaultValue: 'Next' })}
              </ButtonText>
            </Button>
          ) : null}
        </HStack>
      )}
    </VStack>
  );
}

/**
 * One student card in the cohort supervision console — monogram box plus the
 * shared status info. Retained as the no-video variant; `StudentLiveTile`
 * replaces the monogram with the student's live camera (oral-exam-1n-lecturer-ux §3.4).
 */
export function StudentStatusCard({
  summary,
  onNext,
}: StudentStatusCardProps) {
  const { bookingId } = summary;
  return (
    <Box
      p="$3"
      bg="$bgCard"
      borderRadius="$sm"
      borderWidth={1}
      borderColor="$borderLight"
      testID={`student-status-card-${bookingId}`}
    >
      <HStack space="sm" alignItems="flex-start">
        <Box
          w={40}
          h={40}
          borderRadius="$sm"
          bg="$bgBrandSubtle"
          alignItems="center"
          justifyContent="center"
        >
          <Text size="xs" color="$textOnBrandSubtle" fontWeight="bold">
            {studentMonogram(summary)}
          </Text>
        </Box>

        <StudentStatusInfo
          summary={summary}
          onNext={onNext}
        />
      </HStack>
    </Box>
  );
}
